import path from "path";

import { buildActionBegin, ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { IValueProducer } from "../../Commons/TasksCore/BuildTask";
import { ObservableValueProducerBase } from "../../Commons/TasksCore/ChainableValueProducer";
import { ProcessUtils } from "../ProcessUtils/ProcessUtils";
import { ShellProcessHandle } from "../ProcessUtils/ShellProcessHandle";

export class BuildClientJsTask extends ObservableValueProducerBase<string> {
    private readonly pathToWebpackProvider: IValueProducer<string>;
    private useWatch = false;
    private readonly resourcesDirectory: string;
    private buildProcess?: ShellProcessHandle;
    private readonly logger: ILogger;

    public constructor(
        pathToWebpackProvider: IValueProducer<string>,
        resourcesDirectory: string,
        logger: ILogger = getLogger()
    ) {
        super();
        this.pathToWebpackProvider = pathToWebpackProvider;
        this.resourcesDirectory = resourcesDirectory;
        this.logger = logger.withPrefix("CLIENT-JS");
    }

    public async beginListening(): Promise<void> {
        await this.pathToWebpackProvider.beginObserve();
        this.useWatch = true;
    }

    public async stopListening(): Promise<void> {
        this.useWatch = false;
        await this.pathToWebpackProvider.endObserve();
        if (this.buildProcess != undefined) {
            try {
                const buildProcess = this.buildProcess;
                this.buildProcess = undefined;
                await buildProcess.kill();
            } catch (error) {
                this.logger.error(`Failed to stop process: ${error}`);
            }
        }
    }

    public async getCurrentValue(): Promise<string> {
        const webpackConfigFile = await this.pathToWebpackProvider.currentValue;
        if (this.useWatch) {
            this.buildProcess = await this.runWatchProcess(webpackConfigFile);
        } else {
            await this.buildEngineFromSources(webpackConfigFile);
        }
        return webpackConfigFile;
    }

    private async buildEngineFromSources(webpackConfigPath: string): Promise<void> {
        const action = buildActionBegin(this.logger, "Build client.js");
        const workingDirectory = path.dirname(webpackConfigPath);
        await ProcessUtils.execShellCommand(
            "npm",
            ["run", "client:build", "--", `--env`, `resourcesPath=${this.resourcesDirectory}`],
            workingDirectory,
            this.logger
        );
        action.complete();
    }

    private async runWatchProcess(webpackConfigPath: string): Promise<ShellProcessHandle> {
        const workingDirectory = path.dirname(webpackConfigPath);
        const watchProcess = ProcessUtils.createShellProcess(
            "npm",
            ["run", "client:build", "--", `--env`, `resourcesPath=${this.resourcesDirectory}`, "--watch"],
            workingDirectory
        );
        watchProcess.onStdOut.subscribe(output => this.logger.verbose(output));
        this.logger.log("Begin watch");
        watchProcess.start();
        return watchProcess;
    }
}
